<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-account">
    <div class="wy-page-title">{{ $t('account.title') }}</div>
    <el-row>
      <el-col :span="12">
        <div class="account-left">
          <div class="wy-section-title wy-page-subtitle-underline">{{ $t('account.profileTitle') }}</div>
          <div class="content">
            <el-form class="form" ref="profileForm" :model="profileForm" :rules="profileFormRules" :hide-required-asterisk="true">
              <div>
                <el-form-item :label="$t('account.email.label')">
                  <el-input v-model="profileForm.email" size="medium" disabled></el-input>
                </el-form-item>
              </div>
              <div>
                <el-form-item :label="$t('account.firstName.label')" prop="firstName">
                  <el-input v-model="profileForm.firstName" :placeholder="$t('account.firstName.placeholder')" size="medium" @keyup.native="handleInputChanged"></el-input>
                </el-form-item>
                <el-form-item :label="$t('account.lastName.label')" prop="lastName">
                  <el-input v-model="profileForm.lastName" :placeholder="$t('account.lastName.placeholder')" size="medium" @keyup.native="handleInputChanged"></el-input>
                </el-form-item>
              </div>
            </el-form>
            <div class="form-buttons">
              <el-button class="wy-important-button" @click="handleLogout" :disabled="isLoading">{{ $t('button.logout') }}</el-button>
              <el-button class="wy-blue-button" @click="handleUpdateProfile" :disabled="isLoading || !$root.unsaveDataChanages">{{ $t('button.saveChanges') }}</el-button>
            </div>
          </div>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="account-right">
          <div class="wy-section-title wy-page-subtitle-underline">{{ $t('account.passwordTitle') }}</div>
          <div class="content">
            <el-form class="form" ref="passwordForm" :model="passwordForm" :rules="passwordFormRules" :hide-required-asterisk="true" @validate="handlePasswordFormValidated">
              <div class="password-form">
                <el-form-item :label="$t('account.currentPassword.label')" prop="currentPassword" style="margin-bottom: 0;">
                  <el-input type="password" v-model="passwordForm.currentPassword" :placeholder="$t('account.currentPassword.placeholder')" size="medium"></el-input>
                </el-form-item>
              </div>
              <div class="el-form-item__error custom" :style="{'visibility': oldPasswordErrorMsg?'visible':'hidden'}" style="position: relative; height: 22px;">{{oldPasswordErrorMsg}}</div>
              <div class="password-form">
                <el-form-item :label="$t('account.newPassword.label')" prop="newPassword" style="margin-bottom: 0;">
                  <el-input type="password" v-model="passwordForm.newPassword" :placeholder="$t('account.newPassword.placeholder')" size="medium"></el-input>
                </el-form-item>
                <el-form-item :label="$t('account.newPasswordConfirm.label')" prop="newPasswordConfirm" style="margin-bottom: 0;">
                  <el-input type="password" v-model="passwordForm.newPasswordConfirm" :placeholder="$t('account.newPasswordConfirm.placeholder')" size="medium"></el-input>
                </el-form-item>
              </div>
            </el-form>
            <div :class="'password-form-strength ' + newPasswordStrength" :style="{'display': newPasswordStrengthLevel?'block':'none'}">{{newPasswordStrength}}</div>
            <div class="el-form-item__error custom" :style="{'visibility': newPasswordErrorMsg?'visible':'hidden'}" style="position: relative; height: 22px;">{{newPasswordErrorMsg}}</div>
            <div class="form-buttons">
              <el-button class="wy-blue-button update" @click="handleUpdatePassword" :disabled="isLoading || !passwordFormIsValid">{{ $t('button.updatePassword') }}</el-button>
              <el-button class="wy-grey-button" @click="handleForgotPassword" :disabled="isLoading">{{ $t('button.forgotPassword') }}</el-button>
            </div>
            <div class="login-password-hints">
              <div class="title">{{ $t('account.hints.passwordFormatTitle') }}</div>
              <div class="desc" v-html="$t('account.hints.passwordFormatDesc')"></div>              
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
    
    <el-dialog
      :title="$t('login.resetPassword.title')"
      :visible.sync="resetPasswordVisible"
      :show-close="false"
      :closeOnClickModal="false"
      class="wys-dialog-box send-email-dialog"
      width="390px">
      <send-email-popup :controller="module"></send-email-popup>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import AuthService from '../service/auth';
import SendEmailPopup from '../components/user/send-email-popup';

export default Vue.extend({
  name: 'account',
  props: ["accountMenu"],
  components: {
    "send-email-popup": SendEmailPopup
  },

  data() {
    return {
      module: this,
      profileForm: {
        email: '',
        firstName: '',
        lastName: ''
      },
      oldPasswordErrorMsg: null,
      newPasswordErrorMsg: null,
      newPasswordStrengthLevel: 0,

      profileFormRules: {
        firstName: [
          { required: true, message: this.$t('account.firstName.validateEmpty'), trigger: ['blur', 'change'] }
        ],
        lastName: [
          { required: true, message: this.$t('account.lastName.validateEmpty'), trigger: ['blur', 'change'] }
        ]
      },

      passwordFormIsValid: false,
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        newPasswordConfirm: '',
      },
      passwordFormRules: {
        currentPassword: [
          { validator: (rule, value, callback) => {
            if(!this.passwordForm.currentPassword || this.passwordForm.currentPassword == '') {
              this.oldPasswordErrorMsg = this.$t('account.currentPassword.validateEmpty');
              callback(this.oldPasswordErrorMsg);
            }
            else {
              this.oldPasswordErrorMsg = null;
              callback();
            }            
          }, trigger: ['blur', 'change']}
        ],
        newPassword: [
          { validator: (rule, value, callback) => {
            this.validateNewPassword(value, callback);
          }, required: true, trigger: ['blur', 'change']}
        ],
        newPasswordConfirm: [
          { validator: (rule, value, callback) => {
            this.validateNewPassword(value, callback);            
          }, required: true, trigger: ['blur', 'change']}
        ]
      },
      resetPasswordVisible: false,
      isLoading: false
    }
  },

  computed: {
    ...mapGetters(['authUser']),
    newPasswordStrength() {
      if(this.newPasswordStrengthLevel == 1) return 'weak';
      else if(this.newPasswordStrengthLevel == 2) return 'fair';
      else if(this.newPasswordStrengthLevel == 3) return 'good';
      else if(this.newPasswordStrengthLevel == 4) return 'strong';
      else return 'weak';
    }
  },

  methods: {
    ...mapActions(['accountChangePassword']),

    handlePasswordFormValidated(_, isValid) {
      this.passwordFormIsValid = isValid;
    },

    validateNewPassword(value, callback) {
      this.$root.passwordValidator(value, (msg, strength) => {
        this.newPasswordStrengthLevel = strength ? strength : 4;

        if(msg != null && msg != '') {
          this.newPasswordErrorMsg = msg;
          callback(this.newPasswordErrorMsg);
        }
        else if(this.passwordForm.newPasswordConfirm != this.passwordForm.newPassword) {
          this.newPasswordErrorMsg = this.$t('account.newPasswordConfirm.validateDoNotMatch');
          callback(this.newPasswordErrorMsg);
        }
        else {
          this.newPasswordErrorMsg = null;
          callback();
        }
      })
    },

    async handleLogout() {      
      this.$router.checkUnsaveChanges(async () => {
        this.$store.dispatch('logout', 'manual');
        this.$router.replace({name: 'logout'});
      });      
    },

    handleUpdateProfile() {
      this.$refs.profileForm.validate(async (isValid) => {
        if(isValid) {
          this.isLoading = true;
          let res = await AuthService.saveCSRProfile(this.profileForm);
          if(res.data && res.data.id) {
            this.$root.unsaveDataChanages = false;
            this.authUser.user.firstName = this.profileForm.firstName;
            this.authUser.user.lastName = this.profileForm.lastName;
            this.$root.showNotificationLocale("success", "account.alert.updateProfileSuccessTitle", "account.alert.updateProfileSuccessMsg");            
          }
          else {
            this.$root.showNotificationLocale("error", "account.alert.updateProfileFailedTitle", "account.alert.updateProfileFailedMsg");
          }
          this.isLoading = false;
        } 
        else {
          this.$root.showNotificationLocale("error", "account.alert.requiredFieldsTitle", "account.alert.requiredFieldsMsg");
        }
      });      
    },

    async handleUpdatePassword() {
      this.$refs.passwordForm.validate(async (isValid) => {
        if (isValid) {
          let data = {
            "email": this.authUser.user.email,
            "oldPassword": this.passwordForm.currentPassword, 
            "newPassword": this.passwordForm.newPassword,
            "confirmPassword": this.passwordForm.newPasswordConfirm
          }

          this.isLoading = true;
          let res = await this.accountChangePassword(data);
          if(res.status === 200) {
            this.$root.showNotificationLocale("success", "login.resetPassword.resetPasswordSuccessTitle", "login.resetPassword.resetPasswordSuccessMsg");
            this.$refs.passwordForm.resetFields();
          }
          else if(res.data && res.data.errorCode) {
            this.oldPasswordErrorMsg = this.$t(`errorCode.code${res.data.errorCode}`);
          }
          else {
            this.oldPasswordErrorMsg = this.$t('login.resetPassword.resetPasswordFailed');
          }
          this.isLoading = false;
        }
      });
    },

    handleForgotPassword() {
      this.resetPasswordVisible = true;
    },

    handleInputChanged() {
      this.$root.unsaveDataChanages = true;
    }
  },

  created() {
    this.profileForm.email = this.authUser.user.email;
    this.profileForm.firstName = this.authUser.user.firstName;
    this.profileForm.lastName = this.authUser.user.lastName;
  }
});
</script>

<style lang="scss">
.wy-account {
  text-align: left;

  .password-form {
    .el-form-item__error {
      display: none;
    }
  } 
  .el-form-item__error.custom {
    font-size: 11px;
    display: block;
  }
  

  .password-form-strength {
    background-color: green;
    color: white;
    font-size: 12px;
    padding: 1px;
    text-align: center;
    margin: 2px 0px 2px 0px;
  }
  .password-form-strength.weak {
    background: #f45042;
  }
  .password-form-strength.fair {
    background: #efe700;
  }
  .password-form-strength.good {
    background: #24d038;
  }
  .password-form-strength.strong {
    background: #219c30;
  }

  .el-form {
    > div {
      display: flex;
    }
    div {
      width: 100%;
    }
    .el-form-item + .el-form-item {
      margin-left: 30px;
    }
  }

  .wy-section-title{
    font-size: 14px;
  }
  .account-left {
    // margin-right: 20px;
    padding: 20px;
    background-color: #FFF;
    border-radius: 5px;
    height: 310px;
    border: #efefee 1px solid;
    .content {
      min-height: 300px;
      padding: 15px 15px 15px 0;
    }
  }

  .account-right {
    margin-left: 20px;
    padding: 20px;
    background-color: #FFF;
    border-radius: 5px;
    height: 310px;
    border: #efefee 1px solid;
    .content {
      min-height: 300px;
      padding: 15px 15px 15px 0;
    }
  }

  .form-buttons {
    padding-top: 0px;
    .el-button {
      font-size: 12px;
    }
  }

  .el-form-item__label {
    font-size: 14px;
    line-height: 1;
    padding-bottom: 5px !important;
    font-weight: 500;
  }
  .el-input--medium .el-input__inner {
    height: 22px !important;
    line-height: normal !important;
  }
  .el-input.is-disabled .el-input__inner{
    background-color: #FFF;
  }

  .login-password-hints {
    margin-top: 10px;
    text-align: left;
    .title {
      font-size: 12px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .desc {
      font-size: 11px;        
    }
  }
}
</style>